import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { articles as mockArticles, authors as mockAuthors } from "@/mocks/articles";
import type { Article, Author } from "@/mocks/articles";

function rowToArticle(row: Record<string, unknown>): Article {
  return {
    id: row.id as string,
    title: row.title as string,
    subtitle: row.subtitle as string | undefined,
    slug: row.slug as string,
    excerpt: (row.excerpt as string) || "",
    content: (row.content as string) || "",
    category: (row.category as string) || "",
    categorySlug: (row.category_slug as string) || "",
    subcategory: row.subcategory as string | undefined,
    author: (row.author as string) || "",
    authorSlug: (row.author_slug as string) || "",
    authorRole: row.author_role as string | undefined,
    authorBio: row.author_bio as string | undefined,
    authorImage: row.author_image as string | undefined,
    imageUrl: (row.image_url as string) || "",
    imageAlt: row.image_alt as string | undefined,
    gallery: (row.gallery as string[]) || [],
    publishedAt: (row.published_at as string) || new Date().toISOString(),
    updatedAt: row.updated_at as string | undefined,
    readTime: (row.read_time as string) || "1 Min.",
    views: (row.views as number) || 0,
    tags: (row.tags as string[]) || [],
    status: (row.status as Article["status"]) || "published",
    isFeatured: (row.is_featured as boolean) || false,
    isBreaking: (row.is_breaking as boolean) || false,
    isTopNews: (row.is_top_news as boolean) || false,
    isOpinion: (row.is_opinion as boolean) || false,
    isColumn: (row.is_column as boolean) || false,
    showOnHomepageSlider: (row.show_on_homepage_slider as boolean) || false,
    showInTopNewsColumn: (row.show_in_top_news_column as boolean) || false,
    showInCategoryFeatured: (row.show_in_category_featured as boolean) || false,
    commentsEnabled: row.comments_enabled !== false,
    adsEnabled: row.ads_enabled !== false,
    sources: (row.sources as Article["sources"]) || [],
    internalLinks: (row.internal_links as Article["internalLinks"]) || [],
    externalLinks: (row.external_links as Article["externalLinks"]) || [],
    relatedArticleIds: (row.related_article_ids as string[]) || [],
    metaTitle: row.meta_title as string | undefined,
    metaDescription: row.meta_description as string | undefined,
    focusKeyword: row.focus_keyword as string | undefined,
    canonicalUrl: row.canonical_url as string | undefined,
    ogTitle: row.og_title as string | undefined,
    ogDescription: row.og_description as string | undefined,
    ogImage: row.og_image as string | undefined,
    twitterImage: row.twitter_image as string | undefined,
    noIndex: (row.no_index as boolean) || false,
  };
}

/** Fetch all published articles, ordered by published_at desc */
export function usePublishedArticles(limit?: number) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    let query = supabase
      .from("articles")
      .select("*")
      .eq("status", "published")
      .order("published_at", { ascending: false });

    if (limit) query = query.limit(limit);

    const { data, error: err } = await query;
    if (err) {
      setError(err.message);
      setArticles([]);
    } else {
      const db = (data ?? []).map(rowToArticle);
      if (db.length === 0) {
        // fallback to mock
        const all = mockArticles
          .filter((a) => a.status === "published")
          .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
        setArticles(limit ? all.slice(0, limit) : all);
      } else {
        setArticles(db);
      }
    }
    setLoading(false);
  }, [limit]);

  useEffect(() => { fetch(); }, [fetch]);
  return { articles, loading, error, refetch: fetch };
}

/** Fetch published articles for a specific category slug */
export function useArticlesByCategory(categorySlug: string | undefined) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!categorySlug) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    supabase
      .from("articles")
      .select("*")
      .eq("status", "published")
      .eq("category_slug", categorySlug)
      .order("published_at", { ascending: false })
      .then(({ data, error: err }) => {
        if (err) {
          setError(err.message);
          setArticles([]);
        } else {
          const db = (data ?? []).map(rowToArticle);
          if (db.length === 0) {
            setArticles(
              mockArticles
                .filter((a) => a.status === "published" && a.categorySlug === categorySlug)
                .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
            );
          } else {
            setArticles(db);
          }
        }
        setLoading(false);
      });
  }, [categorySlug]);

  return { articles, loading, error };
}

/** Fetch a single published article by slug */
export function useArticleBySlug(slug: string | undefined) {
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    supabase
      .from("articles")
      .select("*")
      .eq("slug", slug)
      .eq("status", "published")
      .maybeSingle()
      .then(({ data, error: err }) => {
        if (err) { setError(err.message); setArticle(null); }
        else {
          const db = data ? rowToArticle(data as Record<string, unknown>) : null;
          const fallback = mockArticles.find((a) => a.slug === slug && a.status === "published") || null;
          setArticle(db ?? fallback);
        }
        setLoading(false);
      });
  }, [slug]);

  return { article, loading, error };
}

function rowToAuthor(row: Record<string, unknown>): Author {
  return {
    name: row.name as string,
    slug: row.slug as string,
    role: (row.role as string) || "",
    shortBio: (row.short_bio as string) || "",
    longBio: (row.long_bio as string) || "",
    email: (row.email as string) || "",
    image: (row.avatar_url as string) || (row.image as string) || "",
    articleCount: (row.article_count as number) || 0,
    author_type: (row.author_type as Author["author_type"]) || "autor",
    isActive: row.is_active as boolean,
    socialLinks: (row.social_links as Author["socialLinks"]) || [],
  };
}

/** Fetch author info by slug from public.authors */
export function useAuthorBySlug(slug: string | undefined) {
  const [author, setAuthor] = useState<Author | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    supabase
      .from("authors")
      .select("*")
      .eq("slug", slug)
      .maybeSingle()
      .then(({ data, error: err }) => {
        if (err) {
          setError(err.message);
          setAuthor(null);
        } else if (data) {
          setAuthor(rowToAuthor(data as Record<string, unknown>));
        } else {
          setAuthor(mockAuthors.find((a) => a.slug === slug) || null);
        }
        setLoading(false);
      });
  }, [slug]);

  return { author, loading, error };
}

/** Fetch published articles written by a specific author slug */
export function useArticlesByAuthor(authorSlug: string | undefined, limit?: number) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!authorSlug) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    let query = supabase
      .from("articles")
      .select("*")
      .eq("status", "published")
      .eq("author_slug", authorSlug)
      .order("published_at", { ascending: false });
    if (limit) query = query.limit(limit);
    const { data, error: err } = await query;
    if (err) { setError(err.message); setArticles([]); }
    else {
      const db = (data ?? []).map(rowToArticle);
      if (db.length === 0) {
        let all = mockArticles
          .filter((a) => a.status === "published" && a.authorSlug === authorSlug)
          .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
        if (limit) all = all.slice(0, limit);
        setArticles(all);
      } else {
        setArticles(db);
      }
    }
    setLoading(false);
  }, [authorSlug, limit]);

  useEffect(() => { fetch(); }, [fetch]);
  return { articles, loading, error, refetch: fetch };
}

/** Fetch all active columnists from public.authors */
export function useColumnists() {
  const [columnists, setColumnists] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("authors")
      .select("*")
      .eq("author_type", "kolumnist")
      .eq("is_active", true)
      .order("name");
    if (err) { setError(err.message); setColumnists([]); }
    else {
      setColumnists((data ?? []).map(rowToAuthor));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);
  return { columnists, loading, error, refetch: fetch };
}

/** Fetch all active authors ordered by name */
export function useActiveAuthors(limit?: number) {
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    let query = supabase
      .from("authors")
      .select("*")
      .eq("is_active", true)
      .order("name");
    if (limit) query = query.limit(limit);
    const { data, error: err } = await query;
    if (err) { setError(err.message); setAuthors([]); }
    else {
      setAuthors((data ?? []).map(rowToAuthor));
    }
    setLoading(false);
  }, [limit]);

  useEffect(() => { fetch(); }, [fetch]);
  return { authors, loading, error, refetch: fetch };
}

/** Fetch latest article for each author slug (single query optimized) */
export function useLatestArticlesByAuthors(authorSlugs: string[]) {
  const [latestMap, setLatestMap] = useState<Record<string, { title: string; slug: string; publishedAt: string }>>();
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (authorSlugs.length === 0) { setLoading(false); return; }
    setLoading(true);
    const { data, error: err } = await supabase
      .from("articles")
      .select("author_slug, title, slug, published_at")
      .eq("status", "published")
      .in("author_slug", authorSlugs)
      .order("published_at", { ascending: false });

    if (err) { setLatestMap({}); }
    else {
      const map: Record<string, { title: string; slug: string; publishedAt: string }> = {};
      (data ?? []).forEach((row) => {
        const slug = (row as Record<string, unknown>).author_slug as string;
        if (!map[slug]) {
          map[slug] = {
            title: (row as Record<string, unknown>).title as string,
            slug: (row as Record<string, unknown>).slug as string,
            publishedAt: (row as Record<string, unknown>).published_at as string,
          };
        }
      });
      setLatestMap(map);
    }
    setLoading(false);
  }, [authorSlugs.join(",")]);

  useEffect(() => { fetch(); }, [fetch]);
  return { latestMap, loading };
}

/** Fetch category info by slug */
export function useCategoryBySlug(slug: string | undefined) {
  const [category, setCategory] = useState<{
    id: string; name: string; slug: string; description: string;
    color: string; imageUrl?: string; seoTitle?: string; seoDescription?: string;
    subcategories: { id: string; name: string; slug: string; parentId: string }[];
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    supabase
      .from("categories")
      .select("id, name, slug, description, color, image_url, seo_title, seo_description, subcategories")
      .eq("slug", slug)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setCategory({
            id: data.id as string,
            name: data.name as string,
            slug: data.slug as string,
            description: (data.description as string) || "",
            color: (data.color as string) || "#8B0000",
            imageUrl: data.image_url as string | undefined,
            seoTitle: data.seo_title as string | undefined,
            seoDescription: data.seo_description as string | undefined,
            subcategories: (data.subcategories as { id: string; name: string; slug: string; parentId: string }[]) || [],
          });
        } else {
          // fallback to categoryMeta mock
          const meta = categoryMeta[slug];
          if (meta) {
            setCategory({
              id: slug,
              name: meta.title,
              slug,
              description: meta.description,
              color: meta.color.replace("bg-", "#"),
              imageUrl: undefined,
              seoTitle: undefined,
              seoDescription: undefined,
              subcategories: [],
            });
          } else {
            setCategory(null);
          }
        }
        setLoading(false);
      });
  }, [slug]);

  return { category, loading };
}

const categoryMeta: Record<string, { title: string; description: string; color: string }> = {
  wirtschaft: {
    title: "Wirtschaft",
    description: "Schweizer Wirtschaftsnachrichten, Unternehmensberichte und Marktanalysen.",
    color: "#0f766e",
  },
  "migration-integration": {
    title: "Migration & Integration",
    description: "Einwanderung, Asylpolitik, Integration und gesellschaftlicher Wandel in der Schweiz.",
    color: "#be185d",
  },
  bildung: {
    title: "Bildung",
    description: "Bildungspolitik, Schulen, Universitäten und Forschung in der Schweiz.",
    color: "#0891b2",
  },
  schweiz: {
    title: "Schweiz",
    description: "Aktuelle Nachrichten aus der Schweiz. Politik, Wirtschaft, Gesellschaft und Kultur im Überblick.",
    color: "#8B0000",
  },
  finanzen: {
    title: "Finanzen",
    description: "Börsennews, Aktienanalysen, Wirtschaftsberichte und Finanztipps für die Schweiz.",
    color: "#059669",
  },
  "versicherung-gesundheit": {
    title: "Versicherung & Gesundheit",
    description: "News zu Krankenkassen, Prämien, Gesundheitspolitik und medizinischen Innovationen.",
    color: "#0d9488",
  },
  technologie: {
    title: "Technologie",
    description: "Die neuesten Entwicklungen in Technologie, Software und Digitalisierung.",
    color: "#0369a1",
  },
  "digital-ki": {
    title: "Digital & KI",
    description: "Künstliche Intelligenz, Machine Learning und digitale Transformation in der Schweiz.",
    color: "#7c3aed",
  },
  "arbeit-leben": {
    title: "Arbeit & Leben",
    description: "Arbeitsmarkt, Work-Life-Balance, Karriere und Lifestyle in der Schweiz.",
    color: "#d97706",
  },
  politik: {
    title: "Politik",
    description: "Politische News aus Bern, Wahlen, Abstimmungen und Analysen aus der Schweiz.",
    color: "#475569",
  },
  "deals-angebote": {
    title: "Deals & Angebote",
    description: "Die besten Angebote, Deals und Spartipps für die Schweiz.",
    color: "#ea580c",
  },
  meinung: {
    title: "Meinung",
    description: "Kommentare, Analysen und Meinungsbeiträge zu aktuellen Themen.",
    color: "#8B0000",
  },
  kolumnen: {
    title: "Kolumnen",
    description: "Wöchentliche Kolumnen unserer erfahrenen Autoren.",
    color: "#9f1239",
  },
};